/**
 * Distribution bar chart for cycle-time buckets + summary stats (avg/median).
 */
import type { CycleTimeBucket, CycleTimeSummary } from "../../lib/api";
import { computeBarLayout, linearScale } from "./chartHelpers";

interface Props {
  distribution: CycleTimeBucket[];
  summary: CycleTimeSummary;
}

const W = 320;
const H = 120;
const PAD_LEFT = 36;
const PAD_RIGHT = 8;
const PAD_TOP = 8;
const PAD_BOTTOM = 24;
const PLOT_X = PAD_LEFT;
const PLOT_W = W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = H - PAD_TOP - PAD_BOTTOM;
const PLOT_Y_BOTTOM = PAD_TOP + PLOT_H;

export function CycleTimeChart({ distribution, summary }: Props): preact.JSX.Element {
  if (summary.sample_count === 0) {
    return (
      <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
        Noch keine abgeschlossenen Aufgaben mit Statusverlauf
      </p>
    );
  }

  // Only non-empty buckets up to the last non-zero entry for a clean chart.
  const lastNonZero = distribution.reduce(
    (last, b, i) => (b.count > 0 ? i : last),
    0
  );
  const visible = distribution.slice(0, lastNonZero + 1);

  const maxCount = Math.max(1, ...visible.map((b) => b.count));
  const { barWidth, centres } = computeBarLayout(visible.length, PLOT_X, PLOT_W);

  return (
    <div class="flex flex-col gap-1">
      {/* Summary stats */}
      <div class="flex gap-4 text-label text-[color:var(--color-on-surface-variant)]">
        <span>
          Ø{" "}
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {summary.average_days !== null ? `${summary.average_days.toFixed(1)} Tage` : "—"}
          </span>
        </span>
        <span>
          Median{" "}
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {summary.median_days !== null ? `${summary.median_days.toFixed(1)} Tage` : "—"}
          </span>
        </span>
        <span>
          n ={" "}
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {summary.sample_count}
          </span>
        </span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto" }}
        aria-label="Durchlaufzeit-Verteilung"
      >
        {/* Y axis baseline */}
        <line
          x1={PLOT_X}
          y1={PLOT_Y_BOTTOM}
          x2={PLOT_X + PLOT_W}
          y2={PLOT_Y_BOTTOM}
          stroke="currentColor"
          stroke-width={0.5}
          opacity={0.3}
        />
        {/* Y labels */}
        <text
          x={PLOT_X - 3}
          y={PAD_TOP}
          text-anchor="end"
          dominant-baseline="middle"
          font-size="9"
          fill="currentColor"
          opacity={0.6}
        >
          {maxCount}
        </text>
        <text
          x={PLOT_X - 3}
          y={PLOT_Y_BOTTOM}
          text-anchor="end"
          dominant-baseline="middle"
          font-size="9"
          fill="currentColor"
          opacity={0.6}
        >
          0
        </text>

        {/* Bars */}
        {visible.map((b, i) => {
          const cx = centres[i]!;
          const barH = Math.max(1, linearScale(b.count, 0, maxCount, 0, PLOT_H));
          const x = cx - barWidth / 2;
          const y = PLOT_Y_BOTTOM - barH;
          const isOverflow = b.bucket_days > 30;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barH}
                rx={2}
                fill="var(--color-secondary)"
                opacity={0.8}
              >
                <title>
                  {isOverflow
                    ? `>30 Tage: ${b.count}`
                    : `${b.bucket_days - 1}–${b.bucket_days} Tage: ${b.count}`}
                </title>
              </rect>
              {/* Show day label only for first, midpoint, last */}
              {(i === 0 || i === visible.length - 1) && (
                <text
                  x={cx}
                  y={H - PAD_BOTTOM + 13}
                  text-anchor="middle"
                  font-size="8"
                  fill="currentColor"
                  opacity={0.55}
                >
                  {isOverflow ? ">30d" : `${b.bucket_days}d`}
                </text>
              )}
            </g>
          );
        })}

        {/* Median marker line */}
        {summary.median_days !== null && visible.length > 0 && (
          (() => {
            const medX = linearScale(
              summary.median_days,
              0,
              visible[visible.length - 1]!.bucket_days,
              PLOT_X,
              PLOT_X + PLOT_W
            );
            return (
              <line
                x1={medX}
                y1={PAD_TOP}
                x2={medX}
                y2={PLOT_Y_BOTTOM}
                stroke="var(--color-warning)"
                stroke-width={1}
                stroke-dasharray="3,2"
                opacity={0.7}
              />
            );
          })()
        )}
      </svg>
    </div>
  );
}
